import { request } from '../http'
import { IDEMPOTENCY_KEY_HEADER } from '../idempotency'
import type { ApiResult } from '../types'
import type {
  ConfirmedPublicationPlanList,
  ConfirmedPublicationPlanRead,
  DeliveryAuditEventList,
  DeliveryProgressProjectionList,
  DeliveryRecoveryRead,
  LinearDestinationCatalogRead,
  LinearOAuthAuthorizationRead,
  LinearReconciliationRunRead,
  LinearWorkspaceConnectionRead,
  PublicationDestinationReconciliationRequest,
  PublicationEffectReconciliationRequest,
  PublicationExecutionRead,
  PublicationPlanConfirmRequest,
  PublicationPlanPreviewRead,
  PublicationPlanSelection,
} from './models'

export interface LinearAuthorizationCallback {
  readonly state: string
  readonly code?: string | undefined
  readonly error?: string | undefined
  readonly error_description?: string | undefined
}

export function beginLinearAuthorization(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LinearOAuthAuthorizationRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/oauth/authorize`,
    method: 'POST',
    signal,
  })
}

export function completeLinearAuthorization(
  workspaceId: string,
  input: LinearAuthorizationCallback,
  signal?: AbortSignal,
): Promise<ApiResult<LinearWorkspaceConnectionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/oauth/callback`,
    query: { ...input },
    signal,
  })
}

export function resumeLinearAuthorization(
  workspaceId: string,
  authorizationAttemptId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LinearWorkspaceConnectionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/oauth/attempts/${encodeURIComponent(authorizationAttemptId)}/resume`,
    method: 'POST',
    signal,
  })
}

export function listLinearWorkspaces(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<ApiResult<readonly LinearWorkspaceConnectionRead[]>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/workspaces`,
    signal,
  })
}

export function revokeLinearWorkspace(
  workspaceId: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LinearWorkspaceConnectionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/workspaces/${encodeURIComponent(connectionId)}/revoke`,
    method: 'POST',
    signal,
  })
}

export function disconnectLinearWorkspace(
  workspaceId: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LinearWorkspaceConnectionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/workspaces/${encodeURIComponent(connectionId)}`,
    method: 'DELETE',
    signal,
  })
}

export function getLinearDestinations(
  workspaceId: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LinearDestinationCatalogRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/workspaces/${encodeURIComponent(connectionId)}/destinations`,
    signal,
  })
}

export function previewPublicationPlan(
  workspaceId: string,
  selection: PublicationPlanSelection,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationPlanPreviewRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/preview`,
    method: 'POST',
    body: selection,
    signal,
  })
}

export function confirmPublicationPlan(
  workspaceId: string,
  body: PublicationPlanConfirmRequest,
  ifMatch: string,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<ApiResult<ConfirmedPublicationPlanRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/confirm`,
    method: 'POST',
    body,
    ifMatch,
    headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
    signal,
  })
}

export function listConfirmedPublicationPlans(
  workspaceId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<ApiResult<ConfirmedPublicationPlanList>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans`,
    query: { limit },
    signal,
  })
}

export function getConfirmedPublicationPlan(
  workspaceId: string,
  planId: string,
  signal?: AbortSignal,
): Promise<ApiResult<ConfirmedPublicationPlanRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}`,
    signal,
  })
}

export function publishConfirmedPlan(
  workspaceId: string,
  planId: string,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationExecutionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}/publish`,
    method: 'POST',
    headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
    signal,
  })
}

export function resumeConfirmedPlan(
  workspaceId: string,
  planId: string,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationExecutionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}/resume`,
    method: 'POST',
    headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
    signal,
  })
}

export function reconcilePublicationDestination(
  workspaceId: string,
  planId: string,
  body: PublicationDestinationReconciliationRequest,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationExecutionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}/destination/reconcile`,
    method: 'POST',
    body,
    signal,
  })
}

export function reconcilePublicationEffect(
  workspaceId: string,
  planId: string,
  effectId: string,
  body: PublicationEffectReconciliationRequest,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationExecutionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}/effects/${encodeURIComponent(effectId)}/reconcile`,
    method: 'POST',
    body,
    signal,
  })
}

export function getPublicationExecution(
  workspaceId: string,
  planId: string,
  signal?: AbortSignal,
): Promise<ApiResult<PublicationExecutionRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/publication-plans/${encodeURIComponent(planId)}/publication`,
    signal,
  })
}

export function listDeliveryProgress(
  workspaceId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<ApiResult<DeliveryProgressProjectionList>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/progress`,
    query: { limit },
    signal,
  })
}

export function reconcileLinearProgress(
  workspaceId: string,
  connectionId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<ApiResult<LinearReconciliationRunRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/linear/workspaces/${encodeURIComponent(connectionId)}/progress/reconcile`,
    method: 'POST',
    query: { limit },
    signal,
  })
}

export function getDeliveryRecovery(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<ApiResult<DeliveryRecoveryRead>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/recovery`,
    signal,
  })
}

export function listDeliveryAudit(
  workspaceId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<ApiResult<DeliveryAuditEventList>> {
  return request({
    path: `/v1/workspaces/${encodeURIComponent(workspaceId)}/delivery/audit`,
    query: { limit },
    signal,
  })
}
